import { getGameEventParams, logEvent } from "../analytics";
import {
  BOOSTER_CELLS_NAMES,
  BOOSTER_CELLS_NAMES_EXTERNAL,
  CELL_STATE,
  Cell,
  INVERSE_CELL_STATE,
  createLeftCell,
  shakeCell,
} from "../cell";
import { MIN_ASPECT_RATIO } from "../config";
import { XLINKS } from "../constants";
import { throwErrorWithStack } from "../crashlytics";
import { difficultyRatingDialog } from "../difficultyFeedback";
import {
  boardEl,
  boostersEl,
  gameEl,
  indicatorsBgEl,
  indicatorsEl,
  menuOpenBtnEl,
  showTipBtnEl,
} from "../elements";
import { addTouchEvent } from "../events";
import { purchaseBooster } from "../iap";
import { POSITIONS } from "../info";
import { showInfo } from "../info/modal";
import {
  getDrawGeometryCb,
  getGeometryCb,
  lvlCfg,
  placement,
  positionObject,
  setLevelIndicators,
} from "../levels";
import { LEVELS } from "../levels/definitions";
import { getEpisodeIdx } from "../levels/episodes";
import { updateLives } from "../lives";
import { getLogger } from "../logging";
import { playQuest } from "../mainMenu";
import { play } from "../map";
import { stack } from "../nav";
import { updateStyles } from "../nav/styles";
import { RateApp } from "../plugins";
import { setStoryLevelIdx } from "../profile";
import {
  PromiseExecutor,
  yieldNextRepaint,
  yieldWithPriority,
} from "../promise";
import {
  convertSvgsTo,
  downloadBlob,
  replayGame,
  saveSvgFrame,
} from "../recordGame";
import "../ripple";
import { initRipple } from "../ripple";
import { showErrorSnackbar } from "../snackbar";
import { addClickToBtn, playMp3 } from "../sound";
import { withSpinner } from "../spinner";
import store from "../store";
import { setColors } from "../style";
import { resetTimerInterval, setTimeLimit } from "../timer";
import {
  catchRandomCat,
  center,
  dropSeries,
  freezeCat,
  shuffle,
} from "./boosters";
import { Cats, setSteps } from "./cats";
import {
  ADD_X_0,
  ADD_X_1,
  ADD_Y_0,
  cellsBlocked,
  cellsFragment,
} from "./cells";
import { selectCell } from "./editor";
import { GAME_MODE, gameEditorState, gameState, levelDef } from "./gameState";
import { hideTipIfNecessary, resetToggleTipTimer } from "./helper";
import { getBoardString } from "./utils";

const log = getLogger("🧩 Game");

/*

Trap the Cat

  _  ,/|
 '\`o.O'
  =(_ _)=
    |U|
   /  |
  //|  \

Can you keep the kitty from running off the game field?

Original Flash game: http://www.gamedesign.jp/flash/chatnoir/chatnoir.html
All original credits to Gamedesign
SVG/Javascript conversion by GF https://codepen.io/ge1doot/
02 August 2015

*/

// blocked-fields, fields, bg-color, text, cat, system-bars

// human plays
async function click(use, x, y) {
  if (gameState.mode === GAME_MODE.EDITOR && gameEditorState.editing) {
    selectCell(use, x, y);
    return;
  }

  resetTip();

  if (gameState.isLocked()) {
    log.debug("Is locked");
    return;
  }
  try {
    gameState.lock();
    await moveCats(x, y);
  } catch (error) {
    throwErrorWithStack(error);
  } finally {
    if (!gameState.cats.lost && !gameState.cats.won) gameState.unlock();
  }
}

async function moveCats(x, y) {
  const cell = gameState.cells[y][x];
  const cellState = cell.state;
  if (cellState != CELL_STATE.FREE || gameState.cats.in(x, y)) return;

  blockCell(cell);
  logEvent("click", getGameEventParams());
  hideTipIfNecessary();
  addLevelIdToPlayedLevels(levelDef.id);

  await gameState.cats.move();
  const catsWon = await gameState.cats.runIfWon();
  if (catsWon) {
    log.debug("Cats won");
    return;
  }
  await yieldNextRepaint();
  saveSvgFrame(gameEl);
  checkGameFramesLength();
  logBoardString();
  const catsLost = await gameState.cats.loseIfLost();
  if (catsLost) log.debug("Cats lost");
  await possiblyShowDifficultyRatingDialog();
  await possiblyReplayGame();
}

function checkGameFramesLength() {
  if (!__DEV__) return;
  log.assert(
    store.gameFrames.length == store.step + 1,
    `gameFrames.length (${store.gameFrames.length}) != store.step + 1 (${
      store.step + 1
    })`
  );
}

export async function possiblyReplayGame() {
  if (gameState.cats.lost && gameState.mode != GAME_MODE.EDITOR)
    await replayGame();
}

async function possiblyShowDifficultyRatingDialog() {
  if (
    gameState.mode == GAME_MODE.STORY &&
    store.episodeIdx > 0 &&
    !store.difficultyFeedbacks.has(store.episodeIdx - 1)
  )
    await difficultyRatingDialog.show();
}

function addLevelIdToPlayedLevels(levelId) {
  if (levelId && gameState.mode != GAME_MODE.EDITOR && store.step == 1)
    store.set("levelsPlayed", store.levelsPlayed.add(levelId));
}

function blockCell(cell) {
  cell.state = CELL_STATE.BLOCKED;
  cell.win = false;
  cell.id.setAttributeNS(XLINKS, "xlink:href", "#blocked-cell");
  shakeCell(cell.id, true);
}

function resetTip() {
  Array.from(boardEl.getElementsByClassName("helper-cell")).forEach((el) => {
    el.setAttributeNS(XLINKS, "xlink:href", "#free-cell");
    el.classList.remove("helper-cell");
  });
  const helperPointerEl = document.getElementById("helper-pointer");
  if (helperPointerEl && helperPointerEl.parentNode)
    helperPointerEl.parentNode.removeChild(helperPointerEl);
  gameState.showTip = false;
  resetToggleTipTimer();
}

// initialize new game
export async function newGame({
  increaseGameCount = true,
  level = null,
  noGameframesReset = false,
} = {}) {
  gameState.lock();
  logEvent("start", getGameEventParams());

  setLevelDef(level);

  indicatorsBgEl.innerHTML = "";
  boostersEl.innerHTML = "";
  store.step = 0;

  if (increaseGameCount) store.gameCount++;

  resetCells(levelDef);
  freeUpErasedOrOutsideCells(levelDef);
  placeCells(levelDef);

  setOutsideCells();
  updateNonBlockableCells(gameState.cells);
  setWinningCells();
  gameState.cats = new Cats(levelDef.catPlacement);

  refreshGame();
  possiblyShowClassicIntro();

  if (!noGameframesReset) store.gameFrames = [];
  saveSvgFrame(gameEl, !noGameframesReset);
  gameState.unlock();
  await yieldWithPriority("background");
  logBoardString();
  logNumNonClickable();
}

function setLevelDef(level) {
  if (!level) {
    level = lvlCfg({
      placements: [],
      catPlacement: placement(null, [], null),
    });
  }
  levelDef.placements = level.placements;
  levelDef.fieldGeometries = level.fieldGeometries;
  levelDef.fieldSize = level.fieldSize;
  levelDef.timeLimit = level.timeLimit;
  levelDef.stepsLimit = level.stepsLimit;
  levelDef.catPlacement = level.catPlacement;
  levelDef.helper = level.helper;
  levelDef.info = level.info;
  levelDef.id = level.id;
  levelDef.metadata = level.metadata;
  levelDef.innerFieldSize = 0;
}

function freeUpErasedOrOutsideCells(levelDef) {
  const erasedOrOutsideStates = [CELL_STATE.ERASE, CELL_STATE.OUTSIDE];
  for (let i = 2; i < levelDef.fieldSize - 2; i++)
    for (let j = 2; j < levelDef.fieldSize - 2; j++)
      if (cbField(i, j)) {
        let isErasedOrOutside = false;
        for (const placement of levelDef.placements) {
          if (!erasedOrOutsideStates.includes(placement.state)) continue;
          for (const [shape, shapeVariable] of placement.shapes)
            if (getGeometryCb(levelDef.fieldSize, shape, shapeVariable)(i, j))
              isErasedOrOutside = true;
        }
        if (isErasedOrOutside) continue;
        gameState.cells[i][j].state = CELL_STATE.FREE;
        levelDef.innerFieldSize += 1;
      }
}

function resetCells(levelDef) {
  gameState.cells = [];
  for (let i = 0; i < levelDef.fieldSize; i++) {
    gameState.cells[i] = [];
    for (let j = 0; j < levelDef.fieldSize; j++)
      gameState.cells[i][j] = new Cell(click, cellsFragment);
  }
}

function placeCells(levelDef) {
  for (const placement of levelDef.placements)
    for (const [shape, shapeVariable] of placement.shapes) {
      const place = (cb) => placeCell(cb, cbField, placement.state);
      positionObject(place, levelDef.fieldSize, shape, shapeVariable);
    }
}

function possiblyShowClassicIntro() {
  if (
    levelDef.info &&
    (gameState.mode != GAME_MODE.CLASSIC ||
      (gameState.mode == GAME_MODE.CLASSIC && !store.classicIntroShown))
  ) {
    _showInfo();
    if (gameState.mode == GAME_MODE.CLASSIC)
      store.set("classicIntroShown", true);
  }
}

function logBoardString() {
  __DEV__ && log.debug(`🧩 Board:\n${getBoardString()}`);
}

function logNumNonClickable() {
  const numNonClickable = getNumNonClickable();
  log.debug(
    `🧩 ${numNonClickable} / ${levelDef.innerFieldSize} non-clickable fields`
  );
}

export function refreshGame() {
  if (store.levelIdx > 0 && levelDef.helper.placement)
    showTipBtnEl.style.display = "block";
  else showTipBtnEl.style.display = "none";
  boardEl.setAttribute(
    "transform",
    `scale(${15 / levelDef.fieldSize}) translate(25 0)`
  );
  boardEl.innerHTML = "";
  updateLives();
  resizeGameEl();
  setTimeLimit(levelDef.timeLimit);
  resetToggleTipTimer();
  setSteps();
  resetTimerInterval();
  draw();
  updateStyles();
  setLevelIndicators();
  gameState.cats.displayRandom();
  gameState.cats.appendShapes();
  setColors();
}

class ResetAnimationPromiseExecutor extends PromiseExecutor {
  #resetActive;
  #start;
  #duration;
  #resolve;

  _promiseCallback = (resolve) => {
    if (this.#resetActive) return resolve();
    this.#resolve = resolve;
    const [steps] = this._args;
    this.#start = null;
    this.#duration = (steps * 10) / store.animationSpeed;
    // gameState.cats.resetDisplay();
    requestAnimationFrame(this.#fadeOut);
  };

  #fadeOut = (timeStamp) => {
    if (this.#start === null) this.#start = timeStamp;
    const elapsed = timeStamp - this.#start;

    const alpha = 1 - elapsed / this.#duration;
    const opacity = alpha;
    const filter = `brightness(${0.3 + opacity * 0.7}) blur(${
      0.1 / (opacity + 1)
    }vmin) opacity(${0.5 + opacity * 0.5})`;
    const filterIndicators = `brightness(${0.7 + opacity * 0.3}) blur(${
      0.2 / (opacity + 1)
    }vmin) opacity(${0.5 + opacity * 0.5})`;

    const lockEls = document.getElementsByClassName("lock");
    const boosterCounterEls =
      document.getElementsByClassName("booster-counter");

    // Apply the filter to all elements
    gameEl.style.filter = filter;
    indicatorsEl.style.filter = filterIndicators;
    menuOpenBtnEl.style.filter = filter;
    for (const lockEl of lockEls) lockEl.style.filter = filterIndicators;
    for (const boosterCounterEl of boosterCounterEls)
      boosterCounterEl.style.filter = filterIndicators;

    if (elapsed < this.#duration) {
      requestAnimationFrame(this.#fadeOut);
    } else {
      menuOpenBtnEl.style.filter = null;
      this.#resetActive = false;
      this.#resolve();
    }
  };
}

const resetAnimationPromiseExecutor = new ResetAnimationPromiseExecutor();

// reset
export async function reset({ steps = 100 } = {}) {
  return resetAnimationPromiseExecutor.run(steps);
}

export function setLevel() {
  if (store.disableAutoLevels) {
    if (store.levelIdx + 1 > store.maxLevelIdx) {
      store.set("maxLevelIdx", store.levelIdx + 1);
      store.episodeIdx = getEpisodeIdx(store.maxLevelIdx);
    }
  } else if (store.levelIdx < LEVELS.length) {
    store.set("levelIdx", store.levelIdx + 1);
    store.episodeIdx = getEpisodeIdx(store.levelIdx);
    if (store.levelIdx > store.maxLevelIdx) logEvent("level_up");
  }
  if (store.levelIdx > store.maxLevelIdx) {
    store.set("maxLevelIdx", store.levelIdx);
    store.episodeIdx = getEpisodeIdx(store.levelIdx);
    if (store.maxLevelIdx == LEVELS.length) {
      log.debug("🧩 Requesting review");
      RateApp.requestReview();
    }
  }
  setStoryLevelIdx(store.maxLevelIdx);
  setLevelIndicators();
}

export const cbField = (i, j) =>
  levelDef.fieldGeometries.some(([fieldShape, fieldShapeVariable]) =>
    getGeometryCb(levelDef.fieldSize, fieldShape, fieldShapeVariable)(i, j)
  );

function placeCell(cb, cbField, newState) {
  const specialStates = [CELL_STATE.ERASE];
  const start = specialStates.includes(newState) ? 1 : 2;
  for (let i = start; i < levelDef.fieldSize - start; i++) {
    for (let j = start; j < levelDef.fieldSize - start; j++) {
      //   if (specialStates.includes(newState))
      //     log.debug(
      //       `Placing ${INVERSE_CELL_STATE[newState]} at ${i},${j}`,
      //       cb(i, j),
      //       cbField(i, j)
      //     );
      const cell = gameState.cells[i][j];
      if (
        cb(i, j) &&
        (cbField(i, j) ||
          [GAME_MODE.COMMUNITY, GAME_MODE.EDITOR].includes(gameState.mode))

        // ||
        //   cell.state == CELL_STATE.OUTSIDE ||
        //   cell.state == CELL_STATE.NON_BLOCKABLE_OUTSIDE
      ) {
        if (newState == CELL_STATE.ERASE) cell.state = CELL_STATE.ERASE;
        else if (cell.state == CELL_STATE.FREE) cell.state = newState;
        else if (newState == CELL_STATE.FREE) {
          cell.state = newState;
          cell.editorMode = false;
        } else if (newState != cell.state && __DEV__)
          log.warn(
            `🧩 Tried to place state ${INVERSE_CELL_STATE[newState]} on state ${
              INVERSE_CELL_STATE[cell.state]
            } of cell ${i},${j}`
          );
      }
    }
  }
}

export function draw() {
  const cbField = (i, j) =>
    levelDef.fieldGeometries.some(([fieldShape, fieldShapeVariable]) =>
      getDrawGeometryCb(
        levelDef.fieldSize,
        fieldShape,
        fieldShapeVariable
      )(i, j)
    );
  for (let i = 1; i < levelDef.fieldSize - 1; i++)
    for (let j = 1; j < levelDef.fieldSize - 1; j++)
      if (cbField(i, j)) gameState.cells[i][j].createElement(i - 2, j - 2);
      else if (gameState.mode === GAME_MODE.EDITOR && gameEditorState.editing)
        gameState.cells[i][j].createElement(i - 2, j - 2, true);
  boardEl.appendChild(cellsFragment);
}

function freezeRandomCat() {
  const cat = gameState.cats.get(
    Math.floor(Math.random() * gameState.cats.num)
  );
  return freezeCat(cat);
}

function addBoosterTouchEvent(el, state, cb, minLevelIdx) {
  const boosterKey = `num${BOOSTER_CELLS_NAMES[state]}Boosters`;
  return addTouchEvent(el, async () => {
    playMp3("click");
    if (store.maxLevelIdx < minLevelIdx) {
      return showInfo({
        el,
        position: POSITIONS.RIGHT,
        text: `Unlock ${BOOSTER_CELLS_NAMES_EXTERNAL[state]} at Level ${
          minLevelIdx + 1
        }.`,
      });
    }
    if (store[boosterKey] == 0) {
      await purchaseBooster(state);
      const boosterCounterEl = document.getElementById(
        `booster-${state}-counter`
      );
      boosterCounterEl.firstChild.innerHTML = store[boosterKey];
      if (store[boosterKey]) {
        document.getElementById(`booster-${state}`).style.opacity = 1;
        document.getElementById(`booster-${state}-2`).style.opacity = 1;
      }
      return;
    }

    if (gameState.isLocked()) return;
    gameState.lock();

    shakeCell(el);
    const cbWrapper = async () => {
      try {
        const res = await cb();
        if (res === false) {
          if (state == CELL_STATE.RANDOM) {
            log.warn("🧩 Booster impossible, retrying another random one");
            return cbWrapper();
          }
          showErrorSnackbar("Booster impossible");
        } else {
          logEvent("booster_used", { booster: state });
          saveSvgFrame(gameEl);
          decrementBoosterCounter(state);
          store.step += 1;
          gameState.cats.checkIfLostQuick();
        }
      } catch (error) {
        log.error(error); // Or handle the error as needed
      } finally {
        gameState.unlock();
      }
    };
    cbWrapper();
  });
}

export const MIN_LEVEL_BOOSTERS = {};
MIN_LEVEL_BOOSTERS[CELL_STATE.DROP] = 13;
MIN_LEVEL_BOOSTERS[CELL_STATE.CENTER] = 27;
MIN_LEVEL_BOOSTERS[CELL_STATE.SHUFFLE] = 39;
MIN_LEVEL_BOOSTERS[CELL_STATE.CATCH] = 51;
MIN_LEVEL_BOOSTERS[CELL_STATE.FREEZE] = 65;
MIN_LEVEL_BOOSTERS[CELL_STATE.UNKNOWN] = 80;

function addBoosterTouchEvents(el, state) {
  if (state == CELL_STATE.DROP)
    addBoosterTouchEvent(
      el,
      state,
      dropSeries,
      MIN_LEVEL_BOOSTERS[CELL_STATE.DROP]
    );
  else if (state == CELL_STATE.CENTER)
    addBoosterTouchEvent(
      el,
      state,
      center,
      MIN_LEVEL_BOOSTERS[CELL_STATE.CENTER]
    );
  else if (state == CELL_STATE.FREEZE)
    addBoosterTouchEvent(
      el,
      state,
      freezeRandomCat,
      MIN_LEVEL_BOOSTERS[CELL_STATE.FREEZE]
    );
  else if (state == CELL_STATE.SHUFFLE)
    addBoosterTouchEvent(
      el,
      state,
      shuffle,
      MIN_LEVEL_BOOSTERS[CELL_STATE.SHUFFLE]
    );
  else if (state == CELL_STATE.CATCH)
    addBoosterTouchEvent(
      el,
      state,
      catchRandomCat,
      MIN_LEVEL_BOOSTERS[CELL_STATE.CATCH]
    );
  else if (state == CELL_STATE.UNKNOWN)
    addBoosterTouchEvent(
      el,
      state,
      () => {},
      MIN_LEVEL_BOOSTERS[CELL_STATE.UNKNOWN]
    );
  else if (state == CELL_STATE.RANDOM)
    addBoosterTouchEvent(
      el,
      state,
      () => {
        let numAvailableBoosters = 0;
        if (store.levelIdx >= MIN_LEVEL_BOOSTERS[CELL_STATE.DROP])
          numAvailableBoosters += 1;
        if (store.levelIdx >= MIN_LEVEL_BOOSTERS[CELL_STATE.CENTER])
          numAvailableBoosters += 1;
        if (store.levelIdx >= MIN_LEVEL_BOOSTERS[CELL_STATE.SHUFFLE])
          numAvailableBoosters += 1;
        if (store.levelIdx >= MIN_LEVEL_BOOSTERS[CELL_STATE.CATCH])
          numAvailableBoosters += 1;
        if (store.levelIdx >= MIN_LEVEL_BOOSTERS[CELL_STATE.FREEZE])
          numAvailableBoosters += 1;
        const randNum = Math.floor(Math.random() * numAvailableBoosters);
        if (randNum == 0) return dropSeries();
        else if (randNum == 1) return center();
        else if (randNum == 2) return shuffle();
        else if (randNum == 3) return catchRandomCat();
        else if (randNum == 4) return freezeRandomCat();
      },
      MIN_LEVEL_BOOSTERS[CELL_STATE.RANDOM]
    );
}

function decrementBoosterCounter(state) {
  const boosterKey = `num${BOOSTER_CELLS_NAMES[state]}Boosters`;
  if (store[boosterKey] == 0) return;
  store.set(boosterKey, store[boosterKey] - 1);
  if (store[boosterKey] == 0) {
    const use = document.getElementById(`booster-use-${state}`);
    use.style.opacity = 0.7;
  }
  const boosterCounterEl = document.getElementById(`booster-${state}-counter`);
  if (boosterCounterEl == null)
    log.error(`🧩 Couldn't find booster counter for ${state}`);
  else boosterCounterEl.firstChild.innerHTML = store[boosterKey];
}

function createBooster(state, y_) {
  const disabled = store.maxLevelIdx < MIN_LEVEL_BOOSTERS[state];
  const oldUse = document.getElementById(`booster-use-${state}`);
  if (oldUse) oldUse.parentNode.removeChild(oldUse);
  const use = createLeftCell(state, y_, store.scale, 1, true, disabled);
  addClickToBtn(use);
  use.setAttributeNS(null, "id", `booster-use-${state}`);
  addBoosterTouchEvents(use, state);
  boostersEl.appendChild(use);
  const bbox = use.getBoundingClientRect();
  let indicator;
  const boosterKey = `num${BOOSTER_CELLS_NAMES[state]}Boosters`;
  if (disabled) {
    indicator = document.createElement("div");
    indicator.setAttribute("class", "lock");
    indicator.setAttribute("id", `booster-${state}-lock`);
    const indicatorImg = document.createElement("img");
    indicatorImg.setAttribute("src", "img/lock.svg");
    indicatorImg.style.width = "2.5vmin";
    indicatorImg.style.height = "2.5vmin";
    indicatorImg.style.position = "absolute";
    indicatorImg.style.top = "1vmin";
    indicatorImg.style.left = "0";
    indicatorImg.style.right = "0";
    indicatorImg.style.margin = "auto";
    indicator.appendChild(indicatorImg);
  } else {
    indicator = document.createElement("div");
    indicator.setAttribute("id", `booster-${state}-counter`);
    indicator.classList.add("booster-counter", "notranslate");

    const counterEl = document.createElement("span");
    const numBoosters = store[boosterKey];
    counterEl.textContent = numBoosters;
    counterEl.classList.add("booster-counter-txt");
    indicator.appendChild(counterEl);

    if (store[boosterKey] == 0) use.style.opacity = 0.7;
  }
  indicator.style.position = "fixed";
  indicator.style.left = `calc(${bbox.left + bbox.width}px - 1vmin)`;
  indicator.style.top = `calc(${bbox.top + bbox.height}px - 3vmin)`;
  gameEl.insertAdjacentElement("afterend", indicator);
  return [use, indicator];
}

export function createIndicator() {
  const oldUse = document.getElementById("indicators-use");
  if (oldUse) oldUse.parentNode.removeChild(oldUse);
  const use = createLeftCell(
    CELL_STATE.INDICATORS,
    -3,
    store.scale,
    2.3,
    false
  );
  use.setAttributeNS(null, "id", "indicators-use");
  indicatorsBgEl.appendChild(use);
  indicatorsBgEl.style.display = "initial";
  const { left, top } = use.getBoundingClientRect();
  indicatorsEl.style.top = `${top}px`;
  indicatorsEl.style.left = `${left}px`;
}

export function createIndicatorSmall() {
  const oldUse = document.getElementById("indicators-use");
  if (oldUse) oldUse.parentNode.removeChild(oldUse);
  const use = createLeftCell(
    CELL_STATE.INDICATORS,
    -2.05,
    store.scale,
    1.5,
    false
  );
  use.setAttributeNS(null, "id", "indicators-use");
  indicatorsBgEl.appendChild(use);
  indicatorsBgEl.style.display = "initial";
  const { left, top } = use.getBoundingClientRect();
  indicatorsEl.style.top = `${top}px`;
  indicatorsEl.style.left = `${left}px`;
}

export function createBackButton() {
  removeBackButton();

  // Create button and icon elements first
  const btn = document.createElement("div");
  btn.id = "community-game-back-button";
  btn.classList.add("button", "ripple", "icons-container", "center-h");

  const backIcon = document.createElement("span");
  backIcon.classList.add("material-symbols-outlined", "notranslate");
  backIcon.textContent = "arrow_back";
  backIcon.style.fontSize = "6vmin";

  // Append children first before reading layout
  btn.appendChild(backIcon);

  // Read layout properties
  const { left, width } = indicatorsEl.getBoundingClientRect();

  // Now perform all write operations
  btn.style.top = "5vmin";
  btn.style.left = `${left + width / 2}px`;
  btn.style.backgroundColor = "lightgray";
  btn.style.borderRadius = "10vmin";
  btn.style.padding = "2.4vmin";
  btn.style.borderStyle = "solid";
  btn.style.borderWidth = "0.5vmin";
  btn.style.borderColor = "black";

  // Insert the button into the DOM
  gameEl.insertAdjacentElement("afterend", btn);

  // Initialize other functionalities
  initRipple();
  addTouchEvent(btn, () => {
    boardEl.innerHTML = "";
    indicatorsBgEl.innerHTML = "";
    stack.pop();
    updateStyles();
  });
}

export function removeBackButton() {
  const oldBtn = document.getElementById("community-game-back-button");
  if (oldBtn) oldBtn.parentNode.removeChild(oldBtn);
}

export function createBoosters() {
  const lockEls = document.getElementsByClassName("lock");
  const boosterCounterEls = document.getElementsByClassName("booster-counter");
  while (lockEls[0]) lockEls[0].parentNode.removeChild(lockEls[0]);
  while (boosterCounterEls[0])
    boosterCounterEls[0].parentNode.removeChild(boosterCounterEls[0]);
  const OFFSET = -0.6;
  createBooster(CELL_STATE.DROP, 0 + OFFSET);
  createBooster(CELL_STATE.CENTER, 1 + OFFSET);
  createBooster(CELL_STATE.SHUFFLE, 2 + OFFSET);
  createBooster(CELL_STATE.FREEZE, 3 + OFFSET);
  createBooster(CELL_STATE.UNKNOWN, 4 + OFFSET);
  //   if (store.maxLevelIdx >= MIN_LEVEL_BOOSTERS[CELL_STATE.SHUFFLE])
  //   createBooster(CELL_STATE.RANDOM, 4 + OFFSET);
}

function _showInfo() {
  showInfo({
    el: gameState.cells[levelDef.info.coords[0]][levelDef.info.coords[1]].use,
    position: levelDef.info.position,
    text: levelDef.info.text,
    persist: true,
    typeWriter: true,
    skipTranslate: gameState.mode == GAME_MODE.COMMUNITY,
  });
}

function setOutsideCells() {
  for (let i = 0; i < levelDef.fieldSize; i++) {
    for (let j = 0; j < levelDef.fieldSize; j++) {
      const isErased = gameState.cells[i][j].state == CELL_STATE.ERASE;
      for (let k = 0; k < 6; k++) {
        const nx = i % 2 ? j + ADD_X_1[k] : j + ADD_X_0[k];
        const ny = i + ADD_Y_0[k];
        if (isErased && gameState.cells[ny][nx].state != CELL_STATE.ERASE)
          gameState.cells[ny][nx].state = CELL_STATE.OUTSIDE;
      }
    }
  }
}

function updateNonBlockableCells(grid) {
  const numRows = grid.length;
  const numCols = grid[0].length;
  const visited = new Set();

  // Function to check if a cell is eligible for DFS
  function isEligibleForDFS(i, j) {
    return (
      i >= 0 &&
      i < numRows &&
      j >= 0 &&
      j < numCols &&
      grid[i][j].state === CELL_STATE.NON_BLOCKABLE &&
      !visited.has(`${i},${j}`)
    );
  }

  // DFS to update the grid
  function dfs(i, j) {
    // Add to visited set
    visited.add(`${i},${j}`);

    // Mark the cell as NON_BLOCKABLE_OUTSIDE
    grid[i][j].state = CELL_STATE.NON_BLOCKABLE_OUTSIDE;

    // Determine the neighbors based on row parity
    const addX = i % 2 === 0 ? ADD_X_0 : ADD_X_1;
    const addY = ADD_Y_0; // ADD_Y_0 is used for both even and odd rows

    // Check all neighbors
    for (let k = 0; k < 6; k++) {
      const ni = i + addY[k],
        nj = j + addX[k];
      if (isEligibleForDFS(ni, nj)) {
        dfs(ni, nj);
      }
    }
  }

  // Helper function to check if a cell is next to an OUTSIDE cell
  function isNextToOutside(i, j) {
    const addX = i % 2 === 0 ? ADD_X_0 : ADD_X_1;
    const addY = ADD_Y_0;

    for (let k = 0; k < 6; k++) {
      const ni = i + addY[k],
        nj = j + addX[k];
      if (
        ni >= 0 &&
        ni < numRows &&
        nj >= 0 &&
        nj < numCols &&
        grid[ni][nj].state === CELL_STATE.OUTSIDE
      ) {
        return true;
      }
    }
    return false;
  }

  // Start DFS from NON_BLOCKABLE cells that are next to OUTSIDE cells
  for (let i = 0; i < numRows; i++) {
    for (let j = 0; j < numCols; j++) {
      if (
        grid[i][j].state === CELL_STATE.NON_BLOCKABLE &&
        !visited.has(`${i},${j}`) &&
        isNextToOutside(i, j)
      ) {
        dfs(i, j);
      }
    }
  }

  return grid;
}

export function setWinningCells() {
  for (let i = 0; i < levelDef.fieldSize; i++) {
    for (let j = 0; j < levelDef.fieldSize; j++) {
      if (cellsBlocked(i, j)) continue;
      for (let k = 0; k < 6; k++) {
        const nx = i % 2 ? j + ADD_X_1[k] : j + ADD_X_0[k];
        const ny = i + ADD_Y_0[k];
        const cellNext = gameState.cells[ny][nx];
        if (
          cellNext.state == CELL_STATE.OUTSIDE ||
          cellNext.state == CELL_STATE.NON_BLOCKABLE_OUTSIDE
        )
          gameState.cells[i][j].win = true;
      }
    }
  }
}

export const getNumNonClickable = () => {
  const freeStates = [CELL_STATE.FREE];
  const erasedOrOutsideStates = [
    CELL_STATE.ERASE,
    CELL_STATE.OUTSIDE,
    // CELL_STATE.NON_BLOCKABLE_OUTSIDE,
  ];
  let numNonClickable = 0;
  for (let i = 2; i < levelDef.fieldSize - 2; i++)
    for (let j = 2; j < levelDef.fieldSize - 2; j++)
      if (
        !freeStates.includes(gameState.cells[i][j].state) &&
        !erasedOrOutsideStates.includes(gameState.cells[i][j].state)
      )
        numNonClickable++;
  for (const cat of gameState.cats.getAll()) {
    const catCell = gameState.cells[cat.y][cat.x];
    if (catCell.state == CELL_STATE.FREE) numNonClickable++;
  }
  return numNonClickable;
};

export const resizeGameEl = () => {
  const screenAspectRatio = store.innerWidth / store.innerHeight;
  if (screenAspectRatio < MIN_ASPECT_RATIO) {
    store.scale = screenAspectRatio / MIN_ASPECT_RATIO;
    const deltaY = (store.innerWidth * (1 - store.scale)) / 1.5;
    gameEl.style.width = null;
    gameEl.style.height = store.innerHeight - deltaY;
    gameEl.style.paddingBottom = deltaY / 2;
    gameEl.style.paddingTop = deltaY / 2;
  } else {
    store.scale = 1;
    gameEl.style.height = store.innerHeight;
    gameEl.style.width = store.innerWidth;
    gameEl.style.paddingBottom = 0;
    gameEl.style.paddingTop = 0;
  }
};

if (__DEV__) {
  const visualizeAllLevels = async () => {
    try {
      playQuest();
      play({ delay: 0, levelIdx: 0 });
      store.gameFrames = [];
      for (let i = 0; i < LEVELS.length; i++) {
        gameEditorState.editing = true;
        gameEditorState.showLoosingFields = true;
        newGame({ level: LEVELS[i], noGameframesReset: true });
        log.debug(`🧩 Level ${i + 1}`);
        if (!gameState.cats.num) throw new Error(`🧩 No cats`);
        log.debug(`--------`);
      }
      store.gameFramesReplaying = [...store.gameFrames];
      const blob = await withSpinner(
        convertSvgsTo({
          type: "image_grid",
          tags: Array.from({ length: LEVELS.length }, (_, i) => i + 1),
          scale: 0.25,
        })
      );
      downloadBlob(blob, "image_grid.png");
    } catch (error) {
      throwErrorWithStack(error);
    }
  };
  window.visualizeAllLevels = visualizeAllLevels;
}
